import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MailCheck } from 'lucide-react';
import { Alert, Button } from '@nx/ui';
import { useI18n } from '@nx/i18n';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { authSucceeded, logoutRequested } from '../store/slices/authSlice';
import { authRepository } from '../services/firebase';

export function VerifyEmail() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { identity } = useAppSelector((s) => s.auth);

  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [checkStatus, setCheckStatus] = useState<'idle' | 'checking' | 'unverified'>('idle');

  const handleResend = async () => {
    setResendStatus('sending');
    try {
      await authRepository.sendVerificationEmail();
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  const handleCheckVerified = async () => {
    setCheckStatus('checking');
    try {
      await authRepository.reloadCurrentUser();
      const fresh = await authRepository.getCurrentIdentity();
      if (fresh?.emailVerified) {
        dispatch(authSucceeded({ identity: fresh, message: null }));
        navigate('/dashboard', { replace: true });
      } else {
        setCheckStatus('unverified');
      }
    } catch {
      setCheckStatus('unverified');
    }
  };

  const handleSignOut = () => {
    dispatch(logoutRequested());
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white border border-[#3D3328]/10 rounded-2xl shadow-lg p-6 md:p-8 text-center">
        <div className="flex justify-center mb-6">
          <div className="rounded-full bg-[#F4D03F]/20 p-4">
            <MailCheck className="h-10 w-10 text-[#3D3328]" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-[#3D3328] mb-2">{t('verifyEmail.title')}</h1>

        <p className="text-sm text-[#78665A] mb-1">{t('verifyEmail.desc')}</p>
        {identity?.email && (
          <p className="text-sm font-semibold text-[#3D3328] mb-3">{identity.email}</p>
        )}
        <p className="text-sm text-[#78665A] mb-6">{t('verifyEmail.desc2')}</p>

        {resendStatus === 'sent' && (
          <Alert variant="success" className="mb-4 text-left">
            {t('verifyEmail.resendSuccess')}
          </Alert>
        )}
        {resendStatus === 'error' && (
          <Alert variant="destructive" className="mb-4 text-left">
            {t('verifyEmail.resendError')}
          </Alert>
        )}
        {checkStatus === 'unverified' && (
          <Alert variant="destructive" className="mb-4 text-left">
            {t('verifyEmail.notYetVerified')}
          </Alert>
        )}

        <div className="flex flex-col gap-3">
          <Button
            onClick={handleCheckVerified}
            disabled={checkStatus === 'checking'}
          >
            {checkStatus === 'checking' ? t('verifyEmail.checking') : t('verifyEmail.checkVerified')}
          </Button>

          <Button
            variant="outline"
            onClick={handleResend}
            disabled={resendStatus === 'sending' || resendStatus === 'sent'}
          >
            {resendStatus === 'sending' ? t('verifyEmail.resending') : t('verifyEmail.resend')}
          </Button>

          <button
            type="button"
            onClick={handleSignOut}
            className="text-sm text-[#78665A] hover:text-[#3D3328] hover:underline mt-2"
          >
            {t('verifyEmail.backToLogin')}
          </button>
        </div>
      </div>
    </div>
  );
}
